import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import { soulWiseTheme } from '../../mui-theme';
import CheckIcon from '@mui/icons-material/Check';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { IconButton } from '@mui/material';

const buttonBase = theme => ({
  boxShadow: 'none',
  textTransform: 'none',
  borderRadius: '51px',
  padding: '8px 16px',
  fontSize: '14px',
  lineHeight: '16.8px',
});

const secondaryButtonBase = {
  color: '#fff',
  border: `1px solid ${soulWiseTheme.palette.white10}`,
  backgroundColor: soulWiseTheme.palette.white10,
  '&:hover': {
    backgroundColor: soulWiseTheme.palette.white20,
    boxShadow: 'none',
  },
  '&.MuiButton-isSelected': {
    border: '2px solid #fff',
  },
  '&.MuiButton-isActivated': {
    border: `1px solid ${soulWiseTheme.palette.greenLight} !important`,
    backgroundColor: `${soulWiseTheme.palette.white10} !important`,
  },
  '&.Mui-disabled': {
    color: `${soulWiseTheme.palette.white10} !important`,
    opacity: '0.5 !important',
  },
};

const primaryButtonBase = {
  fontSize: '16px',
  lineHeight: '19px',
  padding: '8px 16px',
  height: '46px',
  color: soulWiseTheme.palette.sea95,
  fontWeight: 700,
  border: 'none',
  backgroundColor: soulWiseTheme.palette.yellowLight,
  '&:hover': {
    backgroundColor: soulWiseTheme.palette.yellowLight,
    opacity: 0.8,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    opacity: '0.6 !important',
    cursor: 'not-allowed',
    pointerEvents: 'auto',
  },
};

export const PrimaryButton = styled(Button)(({ theme }) => ({
  ...buttonBase(theme),
  ...primaryButtonBase,
}));

export const PrimaryButtonCompact = styled(Button)(({ theme }) => ({
  ...primaryButtonBase,
  ...buttonBase(theme),
  height: 'unset !important',
}));

export const SecondaryButton = styled(Button)(({ theme }) => ({
  ...buttonBase(theme),
  ...secondaryButtonBase,
}));

export const SecondaryIconButton = styled(IconButton)(({ theme }) => ({
  ...buttonBase(theme),
  ...secondaryButtonBase,
  padding: '8px',
  [theme.breakpoints.down('sm')]: {
    padding: '8px',
  },
}));

export const PlainIconButton = styled(IconButton)(({ theme }) => ({
  ...buttonBase(theme),
  padding: '8px',
  fontSize: '1.75rem',
  lineHeight: 'inherit',
  color: '#fff',
  border: 'none',
  '&:hover': {
    backgroundColor: soulWiseTheme.palette.white10,
    boxShadow: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px',
    fontSize: '1.75rem',
    lineHeight: 'inherit',
  },
}));

export const SecondaryTextButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'active',
})(({ theme, active }) => ({
  ...buttonBase(theme),
  color: '#fff',
  border: 'none',
  '&:hover': {
    backgroundColor: soulWiseTheme.palette.white10,
    boxShadow: 'none',
  },
  ...(active && {
    backgroundColor: soulWiseTheme.palette.white10, // Same as :hover
    boxShadow: 'none', // Same as :hover
    '&:hover': {
      backgroundColor: soulWiseTheme.palette.white20,
      boxShadow: 'none',
    },
  }),
}));

export const ChipButton = styled(Button)(({ theme }) => ({
  ...buttonBase(theme),
  color: '#fff',
  border: '2px solid rgba(204, 220, 218, 0.1)' /*#CCDCDA w/alpha 10#*/,
  backgroundColor: 'transparent',
  fontWeight: 500,
  '&:hover': {
    borderColor: '#fff',
    boxShadow: 'none',
  },
  '&.MuiButton-isSelected': {
    border: '2px solid #fff',
    backgroundColor: 'transparent',

    '& .MuiButton-icon': {
      opacity: '1 !important',
    },

    '&:hover': {
      background: soulWiseTheme.palette.white10,
      boxShadow: 'none',
    },
  },
  '& .MuiButton-icon': {
    opacity: 0.5,
  },
}));

export const TextButtonInverted = styled(Button)(({ theme }) => ({
  ...buttonBase(theme),
  color: soulWiseTheme.palette.sea95,
}));

export const SecondaryButtonInverted = styled(Button)(({ theme }) => ({
  ...buttonBase(theme),
  color: soulWiseTheme.palette.white,
  backgroundColor: soulWiseTheme.palette.sea80,
  '&:hover': {
    backgroundColor: soulWiseTheme.palette.sea90,
    boxShadow: 'none',
  },
}));

export const ToggleCheckButton = props => {
  const { id, className, rootClassName, onClick, checked, children, sx } = props;

  const [isChecked, setIsChecked] = useState(checked);

  const checkedClass = isChecked ? 'MuiButton-isSelected' : '';
  const classes = classNames(rootClassName, className, checkedClass);
  const icon = isChecked ? <CheckIcon sx={{ color: '#fff' }} /> : '';

  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  const handleClick = () => {
    setIsChecked(prevChecked => !prevChecked);
    onClick();
  };

  return (
    <ChipButton
      id={id}
      sx={sx}
      className={classes}
      variant="contained"
      onClick={handleClick}
      startIcon={icon}
    >
      {children}
    </ChipButton>
  );
};

ToggleCheckButton.propTypes = {
  text: PropTypes.string,
};
